"use client";
import React, { FC } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import Button from "@/components/shared/Button";
import AnimatedHeroGraphic from "./AnimatedHeroGraphic";
import styles from "./Hero.module.css";

export type HeroButton = {
  text?: string;
  href?: string;
  variant?: "primary" | "secondary";
};

export type HeroSideBySideProps = {
  title?: string;
  description?: string;
  subtitle?: any; // PortableText array
  buttons?: HeroButton[];
  imageUrl?: string;
  layout: "left-right" | "right-left";
  showBackgroundGradient?: boolean;
  titleGradient?: "purple-cyan" | "blue-teal";
};

const defaultTitle = "Solutions for Predictable Software Delivery";
const defaultDescription =
  "Clear delivery systems, strong engineering practices, and efficient workflows give teams the structure they need to move with confidence. These solutions support engineering managers, founders, and product teams that want stable delivery and simple tools.";

const HeroSideBySide: FC<HeroSideBySideProps> = ({
  title = defaultTitle,
  description,
  subtitle,
  buttons,
  imageUrl,
  layout,
  showBackgroundGradient = false,
  titleGradient = "purple-cyan",
}) => {
  // Use description if provided, otherwise try to extract text from subtitle
  const displayText = description || (typeof subtitle === "string" ? subtitle : "");

  // Determine text and image order based on layout
  const isLeftRight = layout === "left-right";
  const textFirst = isLeftRight;

  // Get gradient class based on selection
  const gradientClass =
    titleGradient === "blue-teal" ? styles.heroTitleGradientBlueTeal : styles.heroTitleGradient;

  return (
    <section
      className="relative flex w-full min-w-0 flex-col items-center gap-8 overflow-x-clip md:gap-12 lg:gap-16"
      aria-label="Hero section"
    >
      <div className="relative flex w-full min-w-0 max-w-7xl flex-col items-center px-4 py-6 sm:px-6 lg:flex-row lg:px-8 lg:py-11">
        {/* Text Content */}
        <div className={`relative flex w-full min-w-0 flex-col items-start gap-6 overflow-visible md:gap-8 lg:flex-1 lg:gap-12 ${!textFirst ? "lg:order-2" : ""}`}>
          <header className="flex flex-col max-w-2xl items-start gap-4 md:gap-6 relative w-full flex-[0_0_auto] overflow-visible">
            <div className="flex flex-col items-start gap-3 md:gap-4 relative self-stretch w-full flex-[0_0_auto] overflow-visible pb-1 md:pb-2">
              <h1
                className={`relative font-assistant font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-[64px] tracking-[-0.02em] md:tracking-[-1.28px] leading-[1.2] sm:leading-[1.3] md:leading-[1.15] lg:leading-[1.1] overflow-visible ${gradientClass}`}
                style={{ paddingBottom: '0.1em' }}
              >
                {title}
              </h1>
            </div>

            {/* Subtitle (PortableText) - shown above description if both exist */}
            {subtitle && !description && Array.isArray(subtitle) && subtitle.length > 0 && (
              <div className="relative w-full max-w-lg font-inter font-normal text-slate-300 text-base md:text-lg leading-6 md:leading-7">
                <PortableText
                  value={subtitle}
                  components={{
                    block: {
                      normal: ({ children }) => <p>{children}</p>,
                    },
                    marks: {
                      strong: ({ children }) => (
                        <strong className="font-semibold">{children}</strong>
                      ),
                      em: ({ children }) => <em className="italic">{children}</em>,
                      highlight: ({ children }) => (
                        <mark className="bg-[#7f56d9] text-white px-1.5 py-0.5 rounded font-semibold">
                          {children}
                        </mark>
                      ),
                      link: ({ value, children }) => {
                        const target = value?.blank ? "_blank" : undefined;
                        const rel = value?.blank ? "noopener noreferrer" : undefined;
                        return (
                          <a
                            href={value?.href}
                            target={target}
                            rel={rel}
                            className="text-[#a78bfa] hover:text-[#c4b5fd] underline decoration-[#a78bfa]/50 hover:decoration-[#a78bfa] transition-colors duration-200 ease-in-out font-medium"
                          >
                            {children}
                          </a>
                        );
                      },
                    },
                  }}
                />
              </div>
            )}

            {/* Description (plain text) */}
            {displayText && (
              <p className="relative w-full max-w-lg font-inter font-normal text-slate-300 text-base md:text-lg leading-6 md:leading-7">
                {displayText}
              </p>
            )}

            {buttons && buttons.length > 0 && (
              <nav
                className="inline-flex flex-col sm:flex-row items-start gap-3 sm:gap-[15px] relative flex-[0_0_auto] mt-2 md:mt-4"
                role="navigation"
                aria-label="Call to action buttons"
              >
                {buttons.map((button, index) => (
                  <Button
                    key={index}
                    variant={button.variant || "primary"}
                    href={button.href}
                    ariaLabel={button.text}
                  >
                    {button.text}
                  </Button>
                ))}
              </nav>
            )}
          </header>
        </div>

        {/* Image/Graphic Content */}
        <div
          className={`relative h-[250px] w-full min-w-0 max-w-lg overflow-hidden sm:h-[300px] md:h-[350px] lg:h-[400px] lg:max-w-[583px] lg:basis-[583px] lg:shrink ${textFirst ? "" : "lg:order-1"}`}
          role="img"
          aria-label="Decorative graphic"
        >
          {!imageUrl ? (
            <AnimatedHeroGraphic
              className="w-full h-full"
              width={583}
              height={400}
            />
          ) : (
            <Image
              src={imageUrl}
              alt="Hero graphic"
              width={583}
              height={400}
              className="w-full h-full object-cover"
              priority
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSideBySide;

